import {
  Angle,
  Element,
  ElementType,
  Fixture,
  Group,
  LENGTH_INPUT_REGEX,
  lengthConverter,
  Polyline,
  Rect,
  Symbol as SymbolType,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useAppState } from '../../context/AppState/useAppState';
import { canGroupSelection } from '../../helpers/groupHelpers';
import {
  computeMultiselectBBox,
  formatNumberOneDecimal,
  getDistance,
  rectangleCorners,
  resolvePolylinePoint,
  rotationConverter,
} from '../../helpers/utility';
import { useMeasurement } from '../../hooks/useMeasurement';
import { SelectionModes, ViewBox } from '../../types';
import { KebabDotsIcon, RotationIcon } from '../Toolbar/SvgIcons';
import { ControlGroup, ELEMENT_CONTROLS } from './controlsConfig';
import { InputField, UnitIndicator } from './InputField';
import {
  ArrowsGroup,
  CopyButton,
  DeleteButton,
  DimensionsDropdown,
  FontSizeGroup,
  MirroredGroup,
  MoreDropdown,
  PatternDropdown,
  StrokeColorDropdown,
  StrokeDropdown,
  TextAlignGroup,
  TextColorDropdown,
  GroupButton,
  UngroupButton,
} from './PropertyGroups';

const ControlsContainer = styled.div<{ $left: string; $top: string }>`
  position: absolute;
  left: ${(props) => props.$left};
  top: ${(props) => props.$top};
  transform: translate(-50%, 0);
  z-index: 2;
  background-color: var(--surface-elevated);
  padding: 4px;
  border-radius: 4px;
  color: var(--text);
  display: flex;
  gap: 8px;
  flex-direction: row;
  align-items: start;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(21, 36, 55, 0.2);
`;

const P = styled.p<{ $fontWeight?: string; $isActive?: boolean }>`
  font-weight: ${(props) => props.$fontWeight || '400'};
  margin: 2px 2px;
  white-space: nowrap;
  color: ${(props) => (props.$isActive ? '#fff' : '#000')};
`;

const Property = styled.div<{ $autoHeight?: boolean }>`
  gap: 8px;
  border-radius: 4px;
  border: 1px;
  opacity: 0px;
  font-size: 14px;
  font-weight: 400;
  line-height: 16.94px;
  text-align: left;
  color: rgba(0, 0, 0, 1);
  align-self: center;
  display: flex;
  align-items: center;
  min-height: 36px;
  height: ${(props) => (props.$autoHeight ? 'auto' : '36px')};
`;

const MAX_INPUT_COLUMNS = 4;

const InputGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$columns}, auto);
  column-gap: 0px;
  row-gap: 2px;
  align-items: center;
`;

const InputCell = styled.div`
  display: flex;
  align-items: center;
`;

const ExpandButton = styled.button`
  background-color: var(--surface-elevated);
  border: 1px solid var(--border-light);
  border-radius: 4px;
  min-height: 36px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: var(--text-muted);
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  font-style: italic;

  &:hover {
    border: solid 1px var(--border);
  }
`;

const getControlsPosition = (element: Element | null, viewBox: ViewBox) => {
  const { scale, x: viewBoxX, y: viewBoxY } = viewBox;
  if (!element) {
    return { left: '10px', top: '50%' };
  }

  switch (element.type) {
    case ElementType.Text:
    case ElementType.Rect:
    case ElementType.Symbol:
    case ElementType.Fixture:
    case ElementType.Triangle:
    case ElementType.Trapezoid:
    case ElementType.Angle:
    case ElementType.Group: {
      const corners = rectangleCorners(
        element.x,
        element.y,
        element.width,
        element.height,
        element.rotate
      );
      const centerX = element.x + element.width / 2;
      const bottomPoint = corners.sort((a, b) => b[1] - a[1])[0]; // finds the lowest corner.
      const lowestYpoint = bottomPoint[1];
      return {
        left: `${Math.round((centerX - viewBoxX) / scale)}px`,
        top: `${Math.round((lowestYpoint - viewBoxY) / scale + 50)}px`,
      };
    }
    case ElementType.Polyline: {
      const points = [...element.points].filter(Boolean);
      if (!points.length) return { left: '10px', top: '50%' };
      const lowestPoint = points.sort((a, b) => b.y - a.y)[0];
      const middleXPoint =
        points.reduce((p, cur) => cur.x + p, 0) / points.length;
      return {
        left: `${(middleXPoint - viewBoxX) / scale}px`,
        top: `${(lowestPoint.y - viewBoxY) / scale + 50}px`,
      };
    }
    default: {
      return { left: '10px', top: '50%' };
    }
  }
};

const rotationRegex = /^-?\d*[.,]?\d*º?$/;

const toString = (value: string | number) => {
  const newValue = value.toString();
  return newValue.includes('º') ? newValue : `${newValue}º`;
};

const PolylineLengthGroup = ({ element }: { element: Element }) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const { unitLabel, scalingFactor, formatLength } = useMeasurement();

  if (element.type !== ElementType.Polyline) {
    return null;
  }
  const polyline = element as Polyline;
  const segmentCount = polyline.points.length - 1;
  if (segmentCount < 1) {
    return null;
  }

  const isCollapsed = segmentCount > 1 && !expanded;

  if (isCollapsed) {
    const p0 = resolvePolylinePoint(polyline, 0);
    const p1 = resolvePolylinePoint(polyline, 1);
    const firstSegmentLength =
      p0 && p1
        ? getDistance({ x: p0.x, y: p0.y }, { x: p1.x, y: p1.y }) *
          scalingFactor
        : 0;
    return (
      <Property
        id="property-option"
        title={intl.formatMessage({ id: 'controls.showAllSegments' })}
      >
        <ExpandButton
          type="button"
          aria-label={intl.formatMessage({ id: 'controls.showAllSegments' })}
          onClick={() => setExpanded(true)}
        >
          <span>{formatLength(firstSegmentLength)}</span>
          <span>…</span>
          <KebabDotsIcon />
        </ExpandButton>
      </Property>
    );
  }

  const columns = Math.min(segmentCount, MAX_INPUT_COLUMNS);
  return (
    <Property
      id="property-option"
      title={intl.formatMessage({ id: 'controls.length' })}
      $autoHeight={segmentCount > MAX_INPUT_COLUMNS}
    >
      <InputGrid $columns={columns}>
        {Array.from({ length: segmentCount }).map((_, i) => (
          <InputCell key={`${polyline.id}-segment-${i}`}>
            <P $fontWeight="500" as="span">
              L{segmentCount > 1 ? <sub>{i + 1}</sub> : null}
            </P>
            <P as="span">
              <InputField
                key={`${polyline.id}-length-${i}`}
                element={polyline}
                property={'length'}
                segmentIndex={i}
              />
              {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
            </P>
          </InputCell>
        ))}
      </InputGrid>
    </Property>
  );
};

interface DropdownProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface PropertyGroupComponents {
  FontSizeGroup: React.ComponentType<{ element: Element }>;
  TextAlignGroup: React.ComponentType<{ element: Element }>;
  DimensionsDropdown: React.ComponentType<{ element: Element } & DropdownProps>;
  MirroredGroup: React.ComponentType<{ element: Element }>;
  ArrowsGroup: React.ComponentType<{ element: Element }>;
  PatternDropdown: React.ComponentType<{ element: Element } & DropdownProps>;
  StrokeDropdown: React.ComponentType<{ element: Element } & DropdownProps>;
  StrokeColorDropdown: React.ComponentType<
    { element: Element } & DropdownProps
  >;
  TextColorDropdown: React.ComponentType<{ element: Element } & DropdownProps>;
  MoreDropdown: React.ComponentType<DropdownProps>;
  CopyButton: React.ComponentType;
  DeleteButton: React.ComponentType;
  UngroupButton: React.ComponentType;
}

interface PropertyGroupRendererProps {
  groups: ControlGroup[];
  element: Element;
  components?: PropertyGroupComponents;
}

export const PropertyGroupRenderer = ({
  groups,
  element,
  components = {
    FontSizeGroup,
    TextAlignGroup,
    DimensionsDropdown,
    MirroredGroup,
    ArrowsGroup,
    PatternDropdown,
    StrokeDropdown,
    StrokeColorDropdown,
    TextColorDropdown,
    MoreDropdown,
    CopyButton,
    DeleteButton,
    UngroupButton,
  },
}: PropertyGroupRendererProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const intl = useIntl();
  const { unitLabel } = useMeasurement();
  const toggleDropdown = (id: string) => {
    setOpenDropdown((prev) => (prev === id ? null : id));
  };

  return (
    <>
      {groups.map((groupName) => {
        switch (groupName) {
          case 'dimensions':
            return (
              <Property
                key="dimensions"
                id="property-option"
                title={intl.formatMessage({ id: 'controls.widthAndHeight' })}
              >
                <P>
                  <P $fontWeight="500" as="span">
                    B × H
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-width`}
                      element={element as Rect | Fixture | SymbolType | Group}
                      property={'width'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                    ×
                    <InputField
                      key={`${element.id}-height`}
                      element={element as Rect | Fixture | SymbolType | Group}
                      property={'height'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                  </P>
                </P>
              </Property>
            );
          case 'polylineLength':
            return <PolylineLengthGroup key={element.id} element={element} />;
          case 'rotation':
            return (
              <Property
                key="rotation"
                id="property-option"
                title={intl.formatMessage({ id: 'controls.rotation' })}
              >
                <P id="property-option">
                  <P as="span" id="property-option">
                    <RotationIcon color={'#152437'} isFill={false} />
                  </P>
                  <P as="span" id="property-option">
                    <InputField
                      key={`${element.id}-rotate`}
                      element={
                        element as
                          | Rect
                          | Fixture
                          | SymbolType
                          | Triangle
                          | Trapezoid
                          | Angle
                          | Group
                      }
                      property={'rotate'}
                      format={toString}
                      converter={rotationConverter}
                      regex={rotationRegex}
                    />
                  </P>
                </P>
              </Property>
            );
          case 'showDimensions':
            return (
              <components.DimensionsDropdown
                key="showDimensions"
                element={element}
                isOpen={openDropdown === 'showDimensions'}
                onToggle={() => toggleDropdown('showDimensions')}
              />
            );
          case 'mirrored':
            return (
              <components.MirroredGroup key="mirrored" element={element} />
            );
          case 'pattern':
            return (
              <components.PatternDropdown
                key="pattern"
                element={element}
                isOpen={openDropdown === 'pattern'}
                onToggle={() => toggleDropdown('pattern')}
              />
            );
          case 'stroke':
            return (
              <components.StrokeDropdown
                key="stroke"
                element={element}
                isOpen={openDropdown === 'stroke'}
                onToggle={() => toggleDropdown('stroke')}
              />
            );
          case 'strokeColor':
            return (
              <components.StrokeColorDropdown
                key="strokeColor"
                element={element}
                isOpen={openDropdown === 'strokeColor'}
                onToggle={() => toggleDropdown('strokeColor')}
              />
            );
          case 'fontSize':
            return (
              <Property
                key="fontSize"
                id="property-option"
                title={intl.formatMessage({ id: 'controls.fontSize' })}
              >
                <components.FontSizeGroup element={element} />
              </Property>
            );
          case 'textAlign':
            return (
              <Property
                key="textAlign"
                id="property-option"
                title={intl.formatMessage({ id: 'controls.textAlign' })}
              >
                <components.TextAlignGroup element={element} />
              </Property>
            );
          case 'textColor':
            return (
              <components.TextColorDropdown
                key="textColor"
                element={element}
                isOpen={openDropdown === 'textColor'}
                onToggle={() => toggleDropdown('textColor')}
              />
            );
          case 'arrows':
            return <components.ArrowsGroup key="arrows" element={element} />;
          case 'more':
            return (
              <components.MoreDropdown
                key="more"
                isOpen={openDropdown === 'more'}
                onToggle={() => toggleDropdown('more')}
              />
            );
          case 'copy':
            return <components.CopyButton key="copy" />;
          case 'ungroup':
            return <components.UngroupButton key="ungroup" />;
          case 'triangleDimensions':
            return (
              <Property
                key="triangleDimensions"
                id="property-option"
                title={intl.formatMessage({ id: 'controls.dimensions' })}
              >
                <P>
                  <P $fontWeight="500" as="span">
                    {intl.formatMessage({ id: 'controls.width' })}
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-width`}
                      element={element as Triangle}
                      property={'width'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                  </P>
                  <P $fontWeight="500" as="span">
                    {intl.formatMessage({ id: 'controls.height' })}
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-height`}
                      element={element as Triangle}
                      property={'height'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                  </P>
                  <P $fontWeight="500" as="span">
                    {intl.formatMessage({ id: 'controls.pointRatio' })}
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-pointRatio`}
                      element={element as Triangle}
                      property={'pointRatio'}
                      format={formatNumberOneDecimal}
                      converter={lengthConverter}
                      regex={LENGTH_INPUT_REGEX.metric}
                    />
                    <UnitIndicator>%</UnitIndicator>
                  </P>
                </P>
              </Property>
            );
          case 'trapezoidDimensions':
            return (
              <Property
                key="trapezoidDimensions"
                id="property-option"
                title={intl.formatMessage({ id: 'controls.dimensions' })}
              >
                <P>
                  <P $fontWeight="500" as="span">
                    B&#x2081;
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-topWidth`}
                      element={element as Trapezoid}
                      property={'topWidth'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                  </P>
                  ×
                  <P $fontWeight="500" as="span">
                    B&#x2082;
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-bottomWidth`}
                      element={element as Trapezoid}
                      property={'bottomWidth'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                  </P>
                  ×
                  <P $fontWeight="500" as="span">
                    H
                  </P>
                  <P as="span">
                    <InputField
                      key={`${element.id}-height`}
                      element={element as Trapezoid}
                      property={'height'}
                    />
                    {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
                  </P>
                </P>
              </Property>
            );
          case 'delete':
            return <components.DeleteButton key="delete" />;
          default:
            return null;
        }
      })}
    </>
  );
};

// Modes where a panel pinned under the selection would sit in the way.
// MultiSelecting is deliberately absent: a completed rubber-band selection
// *stays* in MultiSelecting (mouseup only clears the drag rect, see
// MouseEvents.tsx), so treating the mode as transient would hide the panel
// for exactly the selection it exists to serve. The live drag is excluded
// by the drag-rect check below instead, matching Canvas.tsx's own rule.
const TRANSIENT_MODES: SelectionModes[] = [
  SelectionModes.Rotating,
  SelectionModes.Resizing,
  SelectionModes.Updating,
  SelectionModes.Add,
];

export const Controls = () => {
  const {
    appState,
    selectedElements: selectedElement,
    selectionMode,
    selectionCoordinates,
    viewBox,
  } = useAppState();

  // Only while the rubber band is actually being dragged out.
  const isRubberbanding =
    selectionMode.type === SelectionModes.MultiSelecting &&
    selectionCoordinates.startX !== null &&
    selectionCoordinates.currentX !== null;
  const isTransient =
    TRANSIENT_MODES.includes(selectionMode.type) || isRubberbanding;

  // An ad-hoc multiselect has no element of its own to hang controls off,
  // so the panel sits under the selection's bounding box and offers the one
  // action that applies to a selection as a whole: grouping it.
  if (selectedElement.length > 1) {
    if (isTransient || !canGroupSelection(selectedElement, selectionMode)) {
      return null;
    }
    const bbox = computeMultiselectBBox(
      selectedElement
        .map((id) => appState.elements[id])
        .filter((el): el is Element => el !== undefined)
    );
    if (!bbox) return null;

    const { scale, x: viewBoxX, y: viewBoxY } = viewBox;
    return (
      <ControlsContainer
        $left={`${Math.round((bbox.x + bbox.width / 2 - viewBoxX) / scale)}px`}
        $top={`${Math.round((bbox.y + bbox.height - viewBoxY) / scale + 50)}px`}
        id="controls"
        data-wheel-passthrough
      >
        <GroupButton />
      </ControlsContainer>
    );
  }

  const element =
    selectedElement.length > 0 ? appState.elements[selectedElement[0]] : null;

  if (!element) {
    return null;
  }

  const controlGroups = ELEMENT_CONTROLS[element.type];
  const { left, top } = getControlsPosition(element, viewBox);

  return (
    <ControlsContainer
      $left={left}
      $top={top}
      id="controls"
      data-wheel-passthrough
    >
      <PropertyGroupRenderer groups={controlGroups} element={element} />
    </ControlsContainer>
  );
};
